import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { useRecoilValue } from "recoil";
import { studentUserState } from "@/common/states/user";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HealthSummaryResponse } from "@/common/types/healthData";
import { GET_STUDENT_HEALTH_SUMMARY } from "@/common/querykeys";
import { Loading } from "@/components/atoms/Loading";

const schema = yup
  .object({
    student: yup
      .object({
        height: yup.string().nullable().required("키는 필수 입력입니다."),
        weight: yup.string().nullable().required("몸무게는 필수 입력입니다."),
        skeletalMuscleMass: yup
          .string()
          .nullable()
          .required("골격근량은 필수 입력입니다."),
        bodyFatPercentage: yup
          .string()
          .nullable()
          .required("체지방률은 필수 입력입니다."),
        sitAndReach: yup
          .string()
          .nullable()
          .required("몸무게는 필수 입력입니다."),
        leftGripStrength: yup
          .string()
          .nullable()
          .required("왼손 악력은 필수 입력입니다."),
        rightGripStrength: yup
          .string()
          .nullable()
          .required("오른손 악력은 필수 입력입니다."),
      })
      .required("학생 정보는 필수 입력입니다."),
  })
  .required();

export const HealthInfoEditPage = () => {
  /** 📦 State Store */
  const student = useRecoilValue(studentUserState);

  /** 🛣️ Navigation Capability Acquisition */
  const navigate = useNavigate();

  /** 📡 API Layer */
  // 학생 건강 정보 요약 데이터 조회
  const { data: studentHealthSummary, isPending } =
    useQuery<HealthSummaryResponse>({
      queryKey: [GET_STUDENT_HEALTH_SUMMARY],
      queryFn: async () => {
        return await request<any>({
          method: "GET",
          url: `/student-health/summary/${student.id}`,
        });
      },
      enabled: !!student.id,
    });

  const { mutateAsync: updateInfo } = useMutation({
    mutationFn: (data: {
      id: string;
      height: string;
      weight: Date;
      skeletalMuscleMass: string;
      bodyFatPercentage: string;
      sitAndReach: string;
      leftGripStrength: string;
      rightGripStrength: string;
    }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "/physical-exam/update-physical-info",
        data: data,
      });
    },
  });

  const defaultHealthData = {
    height: "0",
    weight: "0",
    skeletalMuscleMass: "0",
    bodyFatPercentage: "0",
    sitAndReach: "0",
    leftGripStrength: "0",
    rightGripStrength: "0",
  };

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      student: {
        height: studentHealthSummary?.health?.physicalMeasurements?.height
          ? String(studentHealthSummary.health.physicalMeasurements.height)
          : defaultHealthData.height,
        weight: studentHealthSummary?.health?.physicalMeasurements?.weight
          ? String(studentHealthSummary.health.physicalMeasurements.weight)
          : defaultHealthData.weight,
        skeletalMuscleMass: studentHealthSummary?.health?.physicalMeasurements
          ?.skeletalMuscleMass
          ? String(
              studentHealthSummary.health.physicalMeasurements
                .skeletalMuscleMass
            )
          : defaultHealthData.skeletalMuscleMass,
        bodyFatPercentage: studentHealthSummary?.health?.physicalMeasurements
          ?.bodyFatPercentage
          ? String(
              studentHealthSummary.health.physicalMeasurements.bodyFatPercentage
            )
          : defaultHealthData.bodyFatPercentage,
        sitAndReach: studentHealthSummary?.health?.fitness?.sitAndReach
          ? String(studentHealthSummary.health.fitness.sitAndReach)
          : defaultHealthData.sitAndReach,
        leftGripStrength: studentHealthSummary?.health?.fitness
          ?.leftGripStrength
          ? String(studentHealthSummary.health.fitness.leftGripStrength)
          : defaultHealthData.leftGripStrength,
        rightGripStrength: studentHealthSummary?.health?.fitness
          ?.rightGripStrength
          ? String(studentHealthSummary.health.fitness.rightGripStrength)
          : defaultHealthData.rightGripStrength,
      },
    },
  });

  const onSubmit = async (data: any) => {
    try {
      await updateInfo({
        id: student.id,
        height: data.student.height,
        weight: data.student.weight,
        skeletalMuscleMass: data.student.skeletalMuscleMass,
        bodyFatPercentage: data.student.bodyFatPercentage,
        sitAndReach: data.student.sitAndReach,
        leftGripStrength: data.student.leftGripStrength,
        rightGripStrength: data.student.rightGripStrength,
      });
      navigate("/setting/health-info");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (studentHealthSummary?.health?.physicalMeasurements) {
      reset({
        student: {
          height: String(
            studentHealthSummary.health.physicalMeasurements.height
          ),
          weight: String(
            studentHealthSummary.health.physicalMeasurements.weight
          ),
          skeletalMuscleMass: String(
            studentHealthSummary.health.physicalMeasurements.skeletalMuscleMass
          ),
          bodyFatPercentage: String(
            studentHealthSummary.health.physicalMeasurements.bodyFatPercentage
          ),
          sitAndReach: String(studentHealthSummary.health.fitness.sitAndReach),
          leftGripStrength: String(
            studentHealthSummary.health.fitness.leftGripStrength
          ),
          rightGripStrength: String(
            studentHealthSummary.health.fitness.rightGripStrength
          ),
        },
      });
    }
  }, [studentHealthSummary, reset]);

  return (
    <>
      {isPending ? (
        <Loading />
      ) : (
        <>
          <MainTemplate
            nav={false}
            subPage={true}
            menuBtn={false}
            pageTitle="내 건강 정보 수정"
          >
            <Form
              submitBtn={
                <Buttons
                  type="submit"
                  size="lg"
                  color="primary100"
                  layout="solid"
                  label="저장"
                  disabled={false}
                  onClick={handleSubmit(onSubmit)}
                />
              }
              backBtn={
                <Buttons
                  type="button"
                  size="lg"
                  color="primary100"
                  layout="text"
                  label="취소"
                  disabled={false}
                  onClick={() => navigate("/setting/health-info")}
                />
              }
            >
              <>
                <FormInputBox formTitle="기본검진 정보 수정">
                  <>
                    {/* 키 */}
                    <Controller
                      name="student.height"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="키(cm)"
                            placeholder="키(cm)를 입력하세요."
                            required={true}
                          />
                        );
                      }}
                    />

                    {/* 몸무게 */}
                    <Controller
                      name="student.weight"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="몸무게(kg)"
                            placeholder="몸무게(kg)를 입력하세요."
                            required={true}
                          />
                        );
                      }}
                    />

                    {/* 골격근량 */}
                    <Controller
                      name="student.skeletalMuscleMass"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="골격근량(kg)"
                            placeholder="골격근량(kg)을 입력하세요."
                          />
                        );
                      }}
                    />

                    {/* 체지방률 */}
                    <Controller
                      name="student.bodyFatPercentage"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="체지방률(%)"
                            placeholder="체지방률(%)을 입력하세요."
                          />
                        );
                      }}
                    />
                  </>
                </FormInputBox>

                <FormInputBox formTitle="윗몸 앞으로 굽히기 검사">
                  <>
                    {/* 윗몸 앞으로 굽히기 검사 */}
                    <Controller
                      name="student.sitAndReach"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const sanitizedValue = value.replace(
                            /[^0-9.-]/g,
                            ""
                          );
                          const numericValue = `${
                            sanitizedValue.startsWith("-") ? "-" : ""
                          }${sanitizedValue.replace(/-/g, "")}`;
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="거리(cm)"
                            placeholder="윗몸 앞으로 굽히기 거리(cm)을 입력하세요."
                          />
                        );
                      }}
                    />
                  </>
                </FormInputBox>

                <FormInputBox formTitle="악력">
                  <>
                    {/* 악력(오른손) */}
                    <Controller
                      name="student.rightGripStrength"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="오른손(kg)"
                            placeholder="오른손 악력(kg)을 입력하세요."
                          />
                        );
                      }}
                    />

                    {/* 악력(왼손) */}
                    <Controller
                      name="student.leftGripStrength"
                      control={control}
                      render={({ field }) => {
                        const handleChange = (e: any) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/[^0-9.]/g, "");
                          field.onChange(numericValue);
                        };

                        return (
                          <TextControl
                            control={control}
                            {...field}
                            onChange={handleChange}
                            label="왼손(kg)"
                            placeholder="왼손 악력(kg)을 입력하세요."
                          />
                        );
                      }}
                    />
                  </>
                </FormInputBox>
              </>
            </Form>
          </MainTemplate>
        </>
      )}
    </>
  );
};
